import { assert } from "@/utils/utils";

import type { Column } from "../data-table/types";

import { CellCheckbox } from "./cell-checkbox";
import { CellDate } from "./cell-date";
import { CellEnum } from "./cell-enum";
import { CellEnumMultiple } from "./cell-enum-multiple";
import { CellNumber } from "./cell-number";
import { CellRichText } from "./cell-rich-text";
import { CellTags } from "./cell-tags";
import { CellText } from "./cell-text";

export interface CellProps<T> {
  item: T;
  patch?: any;
  attr?: keyof T;
  index: () => number;
}

export function Cell<T extends object>(props: {
  column: Column<T>;
  item: T;
  patch?: T;
  index: () => number;
}) {
  if (props.column.cell) {
    const CellComponent = props.column.cell;
    return (
      <CellComponent
        item={props.item}
        patch={props.patch}
        attr={props.column.attr}
        index={props.index}
      />
    );
  }

  const attr = props.column.attr;
  assert(attr != null, "Column without a custom `cell` must define `attr`");

  const p = {
    item: props.item,
    patch: props.patch,
    attr,
    index: props.index,
  };

  if (props.column.type === "string") {
    return (
      <CellText
        {...p}
        displayValue={props.item[attr]}
        multiline={props.column.multiline ? "autosize" : undefined}
      />
    );
  }

  if (props.column.type === "richtext") {
    return (
      <CellRichText
        {...p}
        displayValue={props.item[attr]}
        width={props.column.width}
      />
    );
  }

  if (props.column.type === "number") {
    return <CellNumber {...p} precision={props.column.precision} />;
  }

  if (props.column.type === "datetime" || props.column.type === "date") {
    return (
      <CellDate
        {...p}
        type={props.column.type === "datetime" ? "datetime-local" : "date"}
      />
    );
  }

  if (props.column.type === "boolean") {
    return <CellCheckbox {...p} />;
  }

  if (props.column.type === "enum") {
    return <CellEnum {...p} values={props.column.values} />;
  }

  if (props.column.type === "array") {
    if (props.column.values) {
      return <CellEnumMultiple {...p} values={props.column.values} />;
    } else {
      return <CellTags {...p} copyable={props.column.copyable} />;
    }
  }

  // For types not yet implemented, just render the raw value
  return <>{props.item[attr]}</>;
}
